package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.WorkplaceGeoDto
import se.mittpass.shared.api.UpdateWorkplaceGeoRequest

private val geoScope = MainScope()

@Composable
fun GeoSettingsPage(workplaceId: Long) {
    var geo by remember { mutableStateOf<WorkplaceGeoDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var deleting by remember { mutableStateOf(false) }
    var feedbackMsg by remember { mutableStateOf<String?>(null) }
    var isSuccessMsg by remember { mutableStateOf(true) }
    var latStr by remember { mutableStateOf("") }
    var lngStr by remember { mutableStateOf("") }
    var radiusStr by remember { mutableStateOf("200") }

    fun loadGeo() {
        loading = true
        geoScope.launch {
            geoRepo.getGeo(workplaceId).fold(
                onSuccess = { g ->
                    geo = g
                    latStr = g?.latitude?.toString() ?: ""
                    lngStr = g?.longitude?.toString() ?: ""
                    radiusStr = g?.geofenceRadiusMeters?.toString() ?: "200"
                },
                onFailure = {
                    isSuccessMsg = false
                    feedbackMsg = "Kunde inte ladda GPS-inställningar: ${it.message}"
                }
            )
            loading = false
        }
    }

    LaunchedEffect(workplaceId) { loadGeo() }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        // Feedback Banner
        feedbackMsg?.let { msg ->
            Div({
                style {
                    property("background", if (isSuccessMsg) "var(--rp-ok-bg)" else "var(--rp-err-bg)")
                    property("border", if (isSuccessMsg) "1px solid var(--rp-ok-border)" else "1px solid var(--rp-err-border)")
                    property("color", if (isSuccessMsg) "var(--rp-ok-text)" else "var(--rp-err-text)")
                    property("padding", "0.85rem 1.25rem")
                    property("border-radius", "14px")
                    property("font-weight", "700")
                    property("font-size", "0.9rem")
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.6rem")
                }
            }) {
                FaIcon(
                    if (isSuccessMsg) "fa-solid fa-circle-check" else "fa-solid fa-circle-exclamation",
                    size = "1rem",
                    color = if (isSuccessMsg) "var(--rp-ok-text)" else "var(--rp-err-text)"
                )
                Text(msg)
            }
        }

        // Header
        Div({
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "20px"); property("padding", "1.35rem 1.5rem")
                property("border", "1px solid var(--rp-hl-med)")
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.03)")
            }
        }) {
            H3({ style { property("margin", "0 0 0.25rem 0"); property("font-size", "1.3rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                Text("GPS-inställningar")
            }
            P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem") } }) {
                Text("Ange arbetsplatsens position för geofence-baserad incheckning")
            }
        }

        if (loading) {
            Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-subtle)") } }) { Text("Laddar GPS-inställningar...") }
            return@Div
        }

        // GPS Form Card
        val isConfigured = (geo?.latitude != null && geo?.longitude != null) || (latStr.isNotBlank() && lngStr.isNotBlank())

        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "20px")
                property("padding", "1.5rem")
                property("border", "1px solid var(--rp-hl-med)")
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.03)")
            }
        }) {
            // Status Banner
            Div({
                style {
                    property("background", if (isConfigured) "var(--rp-ok-bg)" else "var(--rp-warn-bg)")
                    property("border", if (isConfigured) "1px solid var(--rp-ok-border)" else "1px solid var(--rp-warn-border)")
                    property("padding", "0.85rem 1.1rem")
                    property("border-radius", "14px")
                    property("margin-bottom", "1.5rem")
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.6rem")
                }
            }) {
                FaIcon(
                    if (isConfigured) "fa-solid fa-location-dot" else "fa-solid fa-triangle-exclamation",
                    size = "1.1rem",
                    color = if (isConfigured) "var(--rp-ok-text)" else "var(--rp-warn-text)"
                )
                Span({
                    style {
                        property("font-weight", "700")
                        property("font-size", "0.88rem")
                        property("color", if (isConfigured) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                    }
                }) {
                    val statusText = if (isConfigured) {
                        "GPS-position aktiv (${geo?.latitude ?: latStr}, ${geo?.longitude ?: lngStr}) — Incheckning kräver närhet inom ${geo?.geofenceRadiusMeters ?: radiusStr}m"
                    } else {
                        "GPS inte konfigurerad — All incheckning tillåts utan platsbekräftelse"
                    }
                    Text(statusText)
                }
            }

            // Input Fields Row
            Div({
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                    property("gap", "1rem")
                    property("margin-bottom", "1.5rem")
                }
            }) {
                Div {
                    Span({ style { geoLabel() } }) { Text("Latitud") }
                    Input(InputType.Text) {
                        value(latStr)
                        onInput { latStr = it.value }
                        attr("placeholder", "59.3293")
                        style { geoInput() }
                    }
                }
                Div {
                    Span({ style { geoLabel() } }) { Text("Longitud") }
                    Input(InputType.Text) {
                        value(lngStr)
                        onInput { lngStr = it.value }
                        attr("placeholder", "18.0686")
                        style { geoInput() }
                    }
                }
                Div {
                    Span({ style { geoLabel() } }) { Text("Radie (meter)") }
                    Input(InputType.Number) {
                        value(radiusStr)
                        onInput { e ->
                            radiusStr = e.value?.toString() ?: "200"
                        }
                        style { geoInput() }
                    }
                }
            }

            // Action Buttons
            Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("flex-wrap", "wrap"); property("align-items", "center") } }) {
                // Get Current Location Button
                Button({
                    onClick {
                        feedbackMsg = null
                        kotlinx.browser.window.asDynamic().__mittpassGeoCallback = { lat: Double?, lng: Double?, err: String? ->
                            if (lat != null && lng != null) {
                                latStr = lat.toString()
                                lngStr = lng.toString()
                                isSuccessMsg = true
                                feedbackMsg = "Position hämtad! Klicka på 'Spara position' för att aktivera GPS-kravet."
                            } else {
                                isSuccessMsg = false
                                feedbackMsg = "Kunde inte hämta position: ${err ?: "Okänt fel"}. Kontrollera webbläsarens platsbehörighet."
                            }
                        }
                        js("""
                            if (navigator.geolocation) {
                                navigator.geolocation.getCurrentPosition(
                                    function(pos) {
                                        window.__mittpassGeoCallback(pos.coords.latitude, pos.coords.longitude, null);
                                    },
                                    function(err) {
                                        window.__mittpassGeoCallback(null, null, err.message);
                                    }
                                );
                            } else {
                                window.__mittpassGeoCallback(null, null, 'Geolocation stöds inte av denna webbläsare');
                            }
                        """)
                    }
                    style {
                        property("padding", "0.7rem 1.35rem")
                        property("border", "2px solid var(--rp-pine)")
                        property("background", "var(--rp-surface)")
                        property("color", "var(--rp-pine)")
                        property("border-radius", "50px")
                        property("font-weight", "800")
                        property("font-size", "0.88rem")
                        property("cursor", "pointer")
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.5rem")
                        property("font-family", "inherit")
                    }
                }) {
                    FaIcon("fa-solid fa-location-crosshairs", size = "0.9rem", color = "var(--rp-pine)")
                    Text("Använd min position")
                }

                // Save Button
                Button({
                    if (saving) disabled()
                    onClick {
                        val parsedLat = latStr.trim().replace(',', '.').toDoubleOrNull()
                        val parsedLng = lngStr.trim().replace(',', '.').toDoubleOrNull()
                        val parsedRadius = radiusStr.trim().toIntOrNull() ?: 200

                        if (parsedLat == null || parsedLng == null) {
                            isSuccessMsg = false
                            feedbackMsg = "Ange giltiga koordinater (t.ex. Latitud 59.3293, Longitud 18.0686)."
                            return@onClick
                        }

                        saving = true
                        feedbackMsg = null
                        geoScope.launch {
                            geoRepo.updateGeo(workplaceId, UpdateWorkplaceGeoRequest(parsedLat, parsedLng, parsedRadius))
                                .onSuccess {
                                    isSuccessMsg = true
                                    feedbackMsg = "GPS-position sparad ($parsedLat, $parsedLng — Radie: ${parsedRadius}m)"
                                    loadGeo()
                                }
                                .onFailure {
                                    isSuccessMsg = false
                                    feedbackMsg = "Kunde inte spara GPS: ${it.message}"
                                }
                            saving = false
                        }
                    }
                    style {
                        property("padding", "0.75rem 1.6rem")
                        property("border", "none")
                        property("background", if (saving) "var(--rp-muted)" else "var(--rp-pine)")
                        property("color", "white")
                        property("border-radius", "50px")
                        property("font-weight", "800")
                        property("font-size", "0.9rem")
                        property("cursor", if (saving) "not-allowed" else "pointer")
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.5rem")
                        property("font-family", "inherit")
                        property("box-shadow", "0 4px 12px rgba(5,150,105,0.3)")
                    }
                }) {
                    FaIcon("fa-solid fa-floppy-disk", size = "0.9rem", color = "var(--rp-surface)")
                    Text(if (saving) "Sparar..." else "Spara position")
                }

                // Delete / Clear Button (Always shown when coordinates exist or are entered)
                if (isConfigured || latStr.isNotEmpty() || lngStr.isNotEmpty()) {
                    Button({
                        if (deleting) disabled()
                        onClick {
                            deleting = true
                            latStr = ""
                            lngStr = ""
                            radiusStr = "200"
                            feedbackMsg = null
                            geoScope.launch {
                                geoRepo.updateGeo(workplaceId, UpdateWorkplaceGeoRequest(null, null, 200))
                                    .onSuccess {
                                        isSuccessMsg = true
                                        feedbackMsg = "GPS-position har tagits bort. Geofence-kravet är inaktiverat."
                                        loadGeo()
                                    }
                                    .onFailure {
                                        isSuccessMsg = false
                                        feedbackMsg = "Kunde inte ta bort GPS: ${it.message}"
                                    }
                                deleting = false
                            }
                        }
                        style {
                            property("padding", "0.7rem 1.35rem")
                            property("border", "2px solid var(--rp-err-border)")
                            property("background", "var(--rp-surface)")
                            property("color", "var(--rp-err-text)")
                            property("border-radius", "50px")
                            property("font-weight", "800")
                            property("font-size", "0.88rem")
                            property("cursor", if (deleting) "not-allowed" else "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.45rem")
                            property("font-family", "inherit")
                        }
                    }) {
                        FaIcon("fa-solid fa-trash-can", size = "0.85rem", color = "var(--rp-err-text)")
                        Text(if (deleting) "Tar bort..." else "Ta bort GPS-position")
                    }
                }
            }
        }

        // Info Guide Card
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "20px")
                property("padding", "1.5rem")
                property("border", "1px solid var(--rp-hl-med)")
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.03)")
            }
        }) {
            H4({ style { property("margin", "0 0 0.85rem 0"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("font-size", "1rem") } }) {
                Text("Hur fungerar GPS-incheckning?")
            }
            val steps = listOf(
                "Ange arbetsplatsens GPS-koordinater ovan (eller använd knappen 'Använd min position')",
                "Välj en godkänd radie (t.ex. standard 200 meter runt arbetsplatsen)",
                "Personal som checkar in måste befinna sig inom radien för att incheckningen ska godkännas",
                "Om GPS inte är konfigurerad tillåts alla incheckningar utan platskontroll"
            )
            steps.forEachIndexed { i, step ->
                Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("align-items", "flex-start"); property("margin-bottom", "0.6rem") } }) {
                    Span({
                        style {
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("width", "24px")
                            property("height", "24px")
                            property("border-radius", "50%")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("font-size", "0.72rem")
                            property("font-weight", "800")
                            property("flex-shrink", "0")
                        }
                    }) { Text("${i + 1}") }
                    Span({ style { property("font-size", "0.85rem"); property("color", "var(--rp-subtle)"); property("line-height", "1.4") } }) { Text(step) }
                }
            }
        }
    }
}

private fun org.jetbrains.compose.web.css.StyleScope.geoLabel() {
    property("display", "block"); property("font-weight", "700"); property("font-size", "0.75rem"); property("color", "var(--rp-subtle)")
    property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem")
}

private fun org.jetbrains.compose.web.css.StyleScope.geoInput() {
    property("width", "100%"); property("padding", "0.65rem 0.85rem"); property("border", "1.5px solid var(--rp-hl-med)")
    property("border-radius", "12px"); property("font-size", "0.92rem"); property("font-weight", "700")
    property("outline", "none"); property("box-sizing", "border-box"); property("font-family", "inherit")
}
